import React, { useState, useEffect, useRef } from 'react'
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  Check,
  X
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  isSameMonth, 
  isSameDay, 
  isToday,
  parseISO,
  formatISO
} from 'date-fns'
import { cn } from '../../utils/cn'

const ModernCalendar = ({ 
  value, 
  onChange, 
  placeholder = "Select date and time",
  className = "",
  disabled = false,
  includeTime = true,
  minDate = null,
  maxDate = null,
  useAbsolutePositioning = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(value ? parseISO(value) : null)
  const [selectedTime, setSelectedTime] = useState(
    value ? format(parseISO(value), 'HH:mm') : format(new Date(), 'HH:mm')
  )
  const [timeInputs, setTimeInputs] = useState({
    hours: value ? format(parseISO(value), 'HH') : format(new Date(), 'HH'),
    minutes: value ? format(parseISO(value), 'mm') : format(new Date(), 'mm')
  })
  
  const containerRef = useRef(null)
  const calendarRef = useRef(null)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 })
  const [isInModal, setIsInModal] = useState(false)

  // Check if we're inside a modal that needs absolute positioning
  const isInsideModal = () => {
    return useAbsolutePositioning
  }

  // Update calendar position when opened
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        const inModal = isInsideModal()
        setIsInModal(inModal)
        
        if (inModal) {
          // Use simpler positioning for modals - just position relative to container
          setCalendarPosition({ top: 0, left: 0 })
        } else {
          // Use viewport positioning for regular pages
          const rect = containerRef.current.getBoundingClientRect()
          const viewportHeight = window.innerHeight
          const calendarHeight = 400 // Approximate height of calendar
          
          // Check if calendar would go below viewport
          const spaceBelow = viewportHeight - rect.bottom
          const shouldOpenUpward = spaceBelow < calendarHeight && rect.top > calendarHeight
          
          setCalendarPosition({
            top: shouldOpenUpward ? rect.top - calendarHeight - 4 : rect.bottom + 4,
            left: Math.max(16, Math.min(rect.left, window.innerWidth - 320 - 16)) // 320 is calendar width
          })
        }
      }
      
      // Use setTimeout to ensure DOM is fully updated
      setTimeout(updatePosition, 0)
      
      // Update position on scroll/resize
      const handleUpdate = () => updatePosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }
  }, [isOpen])

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Update internal state when value changes
  useEffect(() => {
    if (value) {
      const date = parseISO(value)
      setSelectedDate(date)
      setSelectedTime(format(date, 'HH:mm'))
      setTimeInputs({
        hours: format(date, 'HH'),
        minutes: format(date, 'mm')
      })
      setCurrentMonth(date)
    }
  }, [value])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setCurrentMonth(date)
    
    if (!includeTime) {
      // If no time needed, still require manual confirmation
      // Don't emit immediately to prevent auto-approval
      // The user must click confirm button
    }
  }

  const handleTimeChange = (field, value) => {
    const newInputs = { ...timeInputs, [field]: value }
    setTimeInputs(newInputs)
    
    // Validate and update time
    const hours = parseInt(newInputs.hours) || 0
    const minutes = parseInt(newInputs.minutes) || 0
    
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      setSelectedTime(timeStr)
    }
  }

  const handleConfirm = () => {
    if (selectedDate) {
      if (includeTime) {
        const [hours, minutes] = selectedTime.split(':')
        const finalDate = new Date(selectedDate)
        finalDate.setHours(parseInt(hours), parseInt(minutes), 0, 0)
        
        const isoString = formatISO(finalDate)
        onChange(isoString)
      } else {
        // For date-only, use the selected date at midnight
        const finalDate = new Date(selectedDate)
        finalDate.setHours(0, 0, 0, 0)
        
        const isoString = formatISO(finalDate)
        onChange(isoString)
      }
      setIsOpen(false)
    }
  }

  const handleClear = () => {
    setSelectedDate(null)
    setSelectedTime(format(new Date(), 'HH:mm'))
    setTimeInputs({
      hours: format(new Date(), 'HH'),
      minutes: format(new Date(), 'mm')
    })
    onChange('')
    setIsOpen(false)
  }

  const renderCalendarGrid = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const days = []
    let day = startDate

    while (day <= endDate) {
      days.push(day)
      day = addDays(day, 1)
    }

    const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    return (
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setCurrentMonth(subMonths(currentMonth, 1))
            }}
            className="p-2 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-900 dark:hover:text-accent-100 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </motion.button>
          
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setCurrentMonth(addMonths(currentMonth, 1))
            }}
            className="p-2 rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-900 dark:hover:text-accent-100 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </motion.button>
        </div>

        {/* Week days */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="h-8 flex items-center justify-center text-xs font-medium text-neutral-500 dark:text-neutral-400"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isCurrentMonth = isSameMonth(day, currentMonth)
            const isSelected = selectedDate && isSameDay(day, selectedDate)
            const isTodayDate = isToday(day)
            const isPastDate = minDate && day < minDate
            const isFutureDate = maxDate && day > maxDate
            const isDisabled = isPastDate || isFutureDate

            return (
              <motion.button
                type="button"
                key={day.toString()}
                whileHover={!isDisabled ? { scale: 1.05 } : {}}
                whileTap={!isDisabled ? { scale: 0.95 } : {}}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (!isDisabled) handleDateSelect(day)
                }}
                disabled={isDisabled}
                className={cn(
                  'h-8 w-8 rounded-lg text-sm font-medium transition-all duration-200',
                  'flex items-center justify-center relative overflow-hidden',
                  isCurrentMonth 
                    ? 'text-neutral-900 dark:text-neutral-100' 
                    : 'text-neutral-400 dark:text-neutral-600',
                  isSelected && [
                    'bg-gradient-to-r from-[#533AFD] to-[#6c63ff] text-white shadow-lg',
                    'ring-2 ring-[#533AFD]/20 dark:ring-[#6c63ff]/30'
                  ],
                  !isSelected && !isDisabled && [
                    'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                    isTodayDate && 'bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-400'
                  ],
                  isDisabled && 'opacity-40 cursor-not-allowed'
                )}
              >
                <span className="relative z-10">
                  {format(day, 'd')}
                </span>
                
                {/* Today indicator */}
                {isTodayDate && !isSelected && (
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-accent-500 rounded-full" />
                )}
              </motion.button>
            )
          })}
        </div>
      </div>
    )
  }

  const renderTimePicker = () => {
    if (!includeTime) return null

    return (
      <div className="border-t border-neutral-200 dark:border-[#2f2b41] p-4">
        <div className="flex items-center gap-3 mb-3">
          <Clock className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Time
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0"
              max="23"
              value={timeInputs.hours}
              onChange={(e) => handleTimeChange('hours', e.target.value)}
              className="w-12 h-8 text-center text-sm border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-accent-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-neutral-500 dark:text-neutral-400">:</span>
            <input
              type="number"
              min="0"
              max="59"
              value={timeInputs.minutes}
              onChange={(e) => handleTimeChange('minutes', e.target.value)}
              className="w-12 h-8 text-center text-sm border border-neutral-300 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-accent-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          
          <div className="flex-1 text-center">
            <div className="text-lg font-mono font-medium text-neutral-700 dark:text-neutral-300">
              {selectedTime}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const formatDisplayValue = () => {
    if (!selectedDate) return placeholder
    
    if (includeTime) {
      return format(selectedDate, 'MMM d, yyyy \'at\' h:mm a')
    }
    
    return format(selectedDate, 'MMM d, yyyy')
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Trigger Button */}
      <motion.button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!disabled) setIsOpen(!isOpen)
        }}
        disabled={disabled}
        className={cn(
          'w-full px-3 py-2 text-left border rounded-lg transition-all duration-200',
          'bg-white dark:bg-[#221f2f] border-neutral-300 dark:border-[#2f2b41]',
          'hover:border-neutral-400 dark:hover:border-neutral-500',
          'focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent',
          'text-neutral-900 dark:text-neutral-100',
          'flex items-center justify-between',
          disabled && 'opacity-50 cursor-not-allowed',
          isOpen && 'ring-2 ring-accent-500/20 border-accent-500'
        )}
      >
        <span className={cn(
          selectedDate ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-500 dark:text-neutral-400'
        )}>
          {formatDisplayValue()}
        </span>
        
        <CalendarIcon className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
      </motion.button>

      {/* Calendar Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={calendarRef}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={`${isInModal ? 'absolute' : 'fixed'} z-[9999] w-80 bg-white dark:bg-[#1a1625] border border-neutral-200 dark:border-[#2f2b41] rounded-xl shadow-xl backdrop-blur-sm`}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              ...(document.documentElement.classList.contains('dark') && {
                background: 'rgba(26, 22, 37, 0.95)'
              }),
              ...(isInModal ? {
                top: 'calc(100% + 4px)',
                left: 0,
                width: '320px'
              } : {
                top: calendarPosition.top,
                left: calendarPosition.left,
              })
            }}
          >
            {renderCalendarGrid()}
            {renderTimePicker()}
            
            {/* Action buttons */}
            <div className="border-t border-neutral-200 dark:border-[#2f2b41] p-4 flex justify-end gap-2">
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleClear()
                }}
                className="px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-900 dark:hover:text-accent-100 transition-colors rounded-md"
              >
                <X className="h-4 w-4" />
              </motion.button>
              
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleConfirm()
                }}
                disabled={!selectedDate}
                className={cn(
                  'px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-200',
                  'bg-gradient-to-r from-[#533AFD] to-[#6c63ff] text-white',
                  'hover:shadow-lg hover:shadow-[#533AFD]/25',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center gap-1'
                )}
              >
                <Check className="h-4 w-4" />
                Confirm
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ModernCalendar